import * as React from "react";
import {
  Accordion,
  AccordionHeader,
  AccordionItem,
  AccordionPanel,
  Badge,
  Button,
  Text,
  makeStyles,
  tokens,
} from "@fluentui/react-components";
import {
  CheckmarkCircle24Regular,
  Warning24Regular,
  ErrorCircle24Regular,
} from "@fluentui/react-icons";
import { EnrichmentResponse } from "../../../../api/types";

export interface ResultsPanelProps {
  response: EnrichmentResponse;
  onDismiss: () => void;
}

const useStyles = makeStyles({
  root: {
    display: "flex",
    flexDirection: "column",
    gap: "12px",
    padding: "12px 0",
  },
  summary: {
    display: "flex",
    flexDirection: "column",
    gap: "6px",
    padding: "12px",
    backgroundColor: tokens.colorNeutralBackground3,
    borderRadius: tokens.borderRadiusMedium,
  },
  summaryRow: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
  },
  successIcon: {
    color: tokens.colorPaletteGreenForeground1,
  },
  warningIcon: {
    color: tokens.colorPaletteYellowForeground1,
  },
  errorIcon: {
    color: tokens.colorPaletteRedForeground1,
  },
  articleList: {
    listStyleType: "none",
    padding: "0",
    margin: "0",
    display: "flex",
    flexDirection: "column",
    gap: "4px",
  },
  articleItem: {
    padding: "4px 8px",
    backgroundColor: tokens.colorNeutralBackground1,
    borderRadius: tokens.borderRadiusSmall,
  },
});

const ResultsPanel: React.FC<ResultsPanelProps> = ({ response, onDismiss }) => {
  const styles = useStyles();
  const { articles, notFound, errors } = response;

  return (
    <div className={styles.root}>
      {/* Summary */}
      <div className={styles.summary}>
        <div className={styles.summaryRow}>
          <CheckmarkCircle24Regular className={styles.successIcon} />
          <Text weight="semibold">{articles.length} article(s) enriched</Text>
        </div>
        {notFound.length > 0 && (
          <div className={styles.summaryRow}>
            <Warning24Regular className={styles.warningIcon} />
            <Text>{notFound.length} article(s) not found</Text>
          </div>
        )}
        {errors.length > 0 && (
          <div className={styles.summaryRow}>
            <ErrorCircle24Regular className={styles.errorIcon} />
            <Text>{errors.length} error(s)</Text>
          </div>
        )}
      </div>

      {/* Details */}
      {(notFound.length > 0 || errors.length > 0) && (
        <Accordion collapsible>
          {notFound.length > 0 && (
            <AccordionItem value="notFound">
              <AccordionHeader>
                Not Found{" "}
                <Badge appearance="filled" color="warning" style={{ marginLeft: 8 }}>
                  {notFound.length}
                </Badge>
              </AccordionHeader>
              <AccordionPanel>
                <ul className={styles.articleList}>
                  {notFound.map((code) => (
                    <li key={code} className={styles.articleItem}>
                      <Text>{code}</Text>
                    </li>
                  ))}
                </ul>
              </AccordionPanel>
            </AccordionItem>
          )}
          {errors.length > 0 && (
            <AccordionItem value="errors">
              <AccordionHeader>
                Errors{" "}
                <Badge appearance="filled" color="danger" style={{ marginLeft: 8 }}>
                  {errors.length}
                </Badge>
              </AccordionHeader>
              <AccordionPanel>
                <ul className={styles.articleList}>
                  {errors.map((err) => (
                    <li key={err.articleCode} className={styles.articleItem}>
                      <Text weight="semibold">{err.articleCode}</Text>
                      <Text>: {err.message}</Text>
                    </li>
                  ))}
                </ul>
              </AccordionPanel>
            </AccordionItem>
          )}
        </Accordion>
      )}

      <Button appearance="secondary" onClick={onDismiss}>
        Start New Enrichment
      </Button>
    </div>
  );
};

export default ResultsPanel;
